import { createContext, useCallback, useEffect, useState } from 'react'

const FullScreenContext = createContext()

const FullScreenProvider = ({ children }) => {
    const [isFullScreen, setIsFullScreen] = useState(false)
    const [isFullScreenModeEnabled, setIsFullScreenModeEnabled] =
        useState(false)
    const [isDisclaimerIgnored, setIsDisclaimerIgnored] = useState(false)

    const handleFullscreenChange = useCallback(() => {
        setIsFullScreen(!!document.fullscreenElement)
    }, [])

    useEffect(() => {
        document.addEventListener('fullscreenchange', handleFullscreenChange)
        document.addEventListener(
            'webkitfullscreenchange',
            handleFullscreenChange
        ) // Safari
        document.addEventListener('mozFullScreenChange', handleFullscreenChange) // Firefox
        document.addEventListener('msfullscreenchange', handleFullscreenChange) // IE

        return () => {
            document.removeEventListener(
                'fullscreenchange',
                handleFullscreenChange
            )
            document.removeEventListener(
                'webkitfullscreenchange',
                handleFullscreenChange
            ) // Safari
            document.removeEventListener(
                'mozFullScreenChange',
                handleFullscreenChange
            ) // Firefox
            document.removeEventListener(
                'msfullscreenchange',
                handleFullscreenChange
            ) // IE
        }
    }, [handleFullscreenChange])

    // methods to open and close full screen mode
    const openFullScreen = useCallback(() => {
        if (!document.fullscreenElement) {
            const rootElement = document.documentElement
            let req
            if (rootElement.requestFullscreen) {
                req = rootElement.requestFullscreen()
            } else if (rootElement.webkitRequestFullscreen) {
                /* Safari */
                req = rootElement.webkitRequestFullscreen()
            } else if (rootElement.msRequestFullscreen) {
                /* IE11 */
                req = rootElement.msRequestFullscreen()
            }
            // Chromium-only: makes Esc hold-to-exit + delivers it to F14.
            Promise.resolve(req)
                .then(() => navigator.keyboard?.lock?.(['Escape']))
                .catch(() => {})
        }
    }, [])

    const closeFullScreen = useCallback(() => {
        navigator.keyboard?.unlock?.()
        if (document.exitFullscreen && document.fullscreenElement) {
            document.exitFullscreen()
        } else if (document.webkitExitFullscreen) {
            /* Safari */
            document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) {
            /* IE11 */
            document.msExitFullscreen()
        }
    }, [])

    // Full Screen close event listener, it will execute the callback when the full screen mode is closed and full screen mode is enabled
    const onFullScreenClose = callback => {
        const handleExitFullScreen = () => {
            if (!document.fullscreenElement && isFullScreenModeEnabled) {
                callback()
            }
        }
        document.addEventListener('fullscreenchange', handleExitFullScreen)
        document.addEventListener(
            'webkitfullscreenchange',
            handleExitFullScreen
        )
        document.addEventListener('mozFullScreenChange', handleExitFullScreen)
        document.addEventListener('msfullscreenchange', handleExitFullScreen)

        return () => {
            document.removeEventListener(
                'fullscreenchange',
                handleExitFullScreen
            )
            document.removeEventListener(
                'webkitfullscreenchange',
                handleExitFullScreen
            )
            document.removeEventListener(
                'mozFullScreenChange',
                handleExitFullScreen
            )
            document.removeEventListener(
                'msfullscreenchange',
                handleExitFullScreen
            )
        }
    }

    // focus blur event listener
    const onFocusBlurChange = useCallback(callback => {
        const handleVisibilityChange = () => {
            callback(document.hidden)
        }

        const handleFocusChange = () => {
            callback(!document.hidden)
        }

        window.addEventListener('focus', handleFocusChange)
        window.addEventListener('blur', handleFocusChange)

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            )
            window.removeEventListener('focus', handleFocusChange)
            window.removeEventListener('blur', handleFocusChange)
        }
    }, [])

    return (
        <FullScreenContext.Provider
            value={{
                isFullScreen,
                openFullScreen,
                closeFullScreen,
                onFullScreenClose,
                onFocusBlurChange,
                isFullScreenModeEnabled,
                setIsFullScreenModeEnabled,
                isDisclaimerIgnored,
                setIsDisclaimerIgnored,
            }}
        >
            {children}
        </FullScreenContext.Provider>
    )
}

export default FullScreenContext
export { FullScreenProvider }
